import { supabase } from '../lib/supabase';
import type {
  ArrivedDeparture,
  QueueEntry,
  QueueOrderPolicy,
  QueuePublicEntry,
  QueuePublicExperience,
  StartedEntry,
} from '../types/database';

// The public display imports its row shape from here rather than reaching into
// types/database — this module is the contract surface for booking.fn_*.
export type { QueuePublicEntry, QueuePublicExperience };

// Direct RPC client for the customer queue. Goes straight at booking.fn_*
// via the anon key — the RealityOperator app has no Supabase auth.users
// record (operators log in by code), so the JWT-protected booking-queue
// edge function used by the admin SPA isn't reachable from here.
//
// Each call passes the operator_id resolved at login (validate_operator_code).
// The RPC validates the id against operations.operators before mutating.
//
// Contract: RealityDatabase/supabase/migrations/20260904155412_booking_queue_v2_rpcs.sql

interface QueueListResult {
  location_id: string;
  fetched_at: string;
  order_policy: QueueOrderPolicy | null;
  stations: number | null;
  cycle_minutes: number | null;
  entries: QueueEntry[];
}

export interface QueueSnapshot {
  entries: QueueEntry[];
  /** position_only (pure check-in order, the default) or slot_first. */
  orderPolicy: QueueOrderPolicy;
  /** ETA inputs the RPC used: parallel stations and minutes per visitor per station. */
  stations: number;
  cycleMinutes: number;
}

export async function fetchQueue(locationId: string): Promise<QueueSnapshot> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_get_queue', { p_location_id: locationId });
  if (error) throw error;
  const res = data as QueueListResult | null;
  return {
    entries: (res?.entries ?? []) as QueueEntry[],
    orderPolicy: res?.order_policy ?? 'position_only',
    stations: res?.stations ?? 1,
    cycleMinutes: res?.cycle_minutes ?? 25,
  };
}

interface StartedTodayResult {
  location_id: string;
  fetched_at: string;
  entries: StartedEntry[];
}

/** Today's started rows at the location with the PC they run on, newest
 *  event first (fn_get_started_today). Feeds the PC tiles' visitor line. */
export async function fetchStartedToday(locationId: string): Promise<StartedEntry[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_get_started_today', { p_location_id: locationId });
  if (error) throw error;
  return ((data as StartedTodayResult | null)?.entries ?? []) as StartedEntry[];
}

export interface QueuePublicSnapshot {
  entries: QueuePublicEntry[];
  stations: number;
  cycleMinutes: number;
  /** Server clock of the snapshot (ISO). null when the RPC omitted it. */
  fetchedAt: string | null;
}

/** Raw jsonb returned by booking.fn_get_queue_public — contract lives in
 *  RealityDatabase/supabase/migrations/20260910130237_booking_queue_eta_in_progress_and_public_names.sql */
interface QueuePublicResult {
  location_id: string;
  fetched_at: string | null;
  stations: number | null;
  cycle_minutes: number | null;
  entries: QueuePublicEntry[] | null;
}

/** PII-minimal payload for the public display (fn_get_queue_public). Both
 *  display surfaces (standalone Pages build and the Electron second screen)
 *  read this and only this — the operator payload never reaches a TV. */
export async function fetchQueuePublic(locationId: string): Promise<QueuePublicSnapshot> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_get_queue_public', { p_location_id: locationId });
  if (error) throw error;
  const res = data as QueuePublicResult | null;
  // Normalise the two nullable collection fields so callers can map without
  // guards; everything else is rendered verbatim from the server.
  const entries = (res?.entries ?? []).map((e) => ({
    ...e,
    experiences: e.experiences ?? [],
  }));
  return {
    entries,
    stations: res?.stations ?? 1,
    cycleMinutes: res?.cycle_minutes ?? 25,
    fetchedAt: res?.fetched_at ?? null,
  };
}

export interface DequeueResult {
  queue_entry_id: string;
  departure_id: string;
  person_index: number;
  new_state: 'started' | 'removed' | 'completed';
  started_pc_id: string | null;
  /** true when the row was already started: this call moved it to p_pc_id. */
  moved: boolean;
  last_event_at: string;
}

/** Pops one individual on experience launch. Operates on queue_entry_id —
 *  the widget already has it in headEntry/selectedEntry. `pcId` records the
 *  PC the visitor plays on (infra.pc_instances.id); calling it again on an
 *  already-started row with a new pcId MOVES the visitor to that PC. */
export async function dequeueForLaunch(
  queueEntryId: string,
  operatorId: string,
  pcId?: string | null,
): Promise<DequeueResult> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_dequeue_entry', {
      p_queue_entry_id: queueEntryId,
      p_operator_id:    operatorId,
      p_reason:         'launched',
      p_pc_id:          pcId ?? null,
    });
  if (error) throw error;
  return data as DequeueResult;
}

export interface UndoResult {
  restored_count: number;
  group_id: string | null;
  /** The most recent dequeued entry that seeded the undo — compare against a
   *  remembered dispatch to decide whether to also stop its experience. */
  seed_entry_id: string;
  /** PC the seed row was started on (null for removals / unknown). */
  seed_pc_id: string | null;
  was_state: 'started' | 'removed';
  /** Every restored row went back to its ORIGINAL position (or the nearest free slot). */
  restored_positions: {
    queue_entry_id: string;
    person_index: number;
    position: number;
    was_position: number | null;
  }[];
}

export async function undoLastDequeue(
  locationId: string,
  operatorId: string,
): Promise<UndoResult> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_undo_last_dequeue', {
      p_location_id: locationId,
      p_operator_id: operatorId,
    });
  if (error) throw error;
  return data as UndoResult;
}

/** "Terug in wachtrij" for a STARTED row: back to queued at the original
 *  spot (head if it was head). No time window, targets one specific row. */
export async function requeueStarted(
  queueEntryId: string,
  operatorId: string,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_requeue_started', {
      p_queue_entry_id: queueEntryId,
      p_operator_id:    operatorId,
    });
  if (error) throw error;
}

/** "Even weg": keeps state + position, dispatch skips the row. Idempotent. */
/** Asks a waiting visitor to come to the desk: the public display announces
 *  them while the operator checks their experience and language. One call per
 *  location, so this also clears any earlier one. */
export async function callEntry(queueEntryId: string, operatorId: string): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_call_entry', {
      p_queue_entry_id: queueEntryId,
      p_operator_id:    operatorId,
    });
  if (error) throw error;
}

/** Takes the call back (the pop-up closed without a start, or moved to another
 *  visitor). Never raises on an already-cleared or already-started row. */
export async function uncallEntry(queueEntryId: string, operatorId: string): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_uncall_entry', {
      p_queue_entry_id: queueEntryId,
      p_operator_id:    operatorId,
    });
  if (error) throw error;
}

export async function parkEntry(queueEntryId: string, operatorId: string): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_park_entry', {
      p_queue_entry_id: queueEntryId,
      p_operator_id:    operatorId,
    });
  if (error) throw error;
}

/** Visitor is back: the row is dispatchable again at its original spot. */
export async function unparkEntry(queueEntryId: string, operatorId: string): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_unpark_entry', {
      p_queue_entry_id: queueEntryId,
      p_operator_id:    operatorId,
    });
  if (error) throw error;
}

/** Confirms/corrects the play language of a whole departure. `language` is
 *  a 2-letter code in any case (stored lowercase); null clears back to the
 *  booking-locale default. */
export async function setDeparturePlayLanguage(
  departureId: string,
  language: string | null,
  operatorId: string,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_set_departure_play_language', {
      p_departure_id: departureId,
      p_language:     language ? language.trim().toLowerCase() : null,
      p_operator_id:  operatorId,
    });
  if (error) throw error;
}

/** Removes one individual from the queue (state -> removed). */
export async function removeEntry(
  queueEntryId: string,
  operatorId: string,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_dequeue_entry', {
      p_queue_entry_id: queueEntryId,
      p_operator_id:    operatorId,
      p_reason:         'removed',
      p_pc_id:          null,
    });
  if (error) throw error;
}

/** Removes every active member of a departure in one action group, so a
 *  single undo restores them together. */
export async function removeParty(
  departureId: string,
  operatorId: string,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_dequeue_party', {
      p_departure_id: departureId,
      p_operator_id:  operatorId,
      p_reason:       'removed',
      p_pc_id:        null,
    });
  if (error) throw error;
}

/** Enqueues every not-yet-active member of an arrived departure. */
export async function enqueueDeparture(
  departureId: string,
  operatorId: string,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_enqueue_departure', {
      p_departure_id: departureId,
      p_operator_id:  operatorId,
    });
  if (error) throw error;
}

export interface ReorderResult {
  queue_entry_id: string;
  visual_position: number;
  requested_position: number;
  /** true when the move was limited to the entry's own timeslot group
   *  (order_policy = slot_first): the slot dominates the visible order, so a
   *  cross-slot move cannot happen — surface this to the operator. */
  clamped: boolean;
  order_policy: QueueOrderPolicy;
  queue_size: number;
}

/** Moves an entry to a 1-based position in the VISIBLE (fn_get_queue) order. */
export async function reorderQueue(
  queueEntryId: string,
  newPosition: number,
  operatorId: string,
): Promise<ReorderResult> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_reorder_queue', {
      p_queue_entry_id: queueEntryId,
      p_new_position:   newPosition,
      p_operator_id:    operatorId,
    });
  if (error) throw error;
  return data as ReorderResult;
}

/** Today's arrived departures at the location that are not in the queue —
 *  the operator's add-to-queue picker source. */
export async function fetchArrivedNotQueued(
  locationId: string,
): Promise<ArrivedDeparture[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_get_arrived_not_queued', { p_location_id: locationId });
  if (error) throw error;
  return (data ?? []) as ArrivedDeparture[];
}

/** Realtime channel lifecycle as reported by supabase-js. Anything other than
 *  SUBSCRIBED means change notifications may be missed (poll instead). */
export type QueueChannelStatus =
  | 'CONNECTING'
  | 'SUBSCRIBED'
  | 'TIMED_OUT'
  | 'CHANNEL_ERROR'
  | 'CLOSED';

// Subscribe to any change on booking.queue_entries filtered by location.
// Caller refetches via fetchQueue on every notification — payloads carry no
// PII, no point diff-merging. `onStatus` receives every channel status change
// (initially CONNECTING) so callers can fall back to polling while the socket
// is not actually delivering.
export function subscribeToQueue(
  locationId: string,
  onChange: () => void,
  onStatus?: (status: QueueChannelStatus) => void,
): () => void {
  onStatus?.('CONNECTING');
  const channel = supabase
    .channel(`queue-realtime-${locationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'booking',
        table: 'queue_entries',
        filter: `location_id=eq.${locationId}`,
      },
      () => onChange(),
    )
    .subscribe((status) => {
      onStatus?.(status as QueueChannelStatus);
    });

  return () => {
    supabase.removeChannel(channel);
    onStatus?.('CLOSED');
  };
}

// PostgREST surfaces SQL exceptions on `error.message`. Treat "not_queued"
// as a benign race signal (someone already dequeued); return true so callers
// can short-circuit silently instead of surfacing an error.
export function isNotQueuedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const msg = (err as { message?: string }).message ?? '';
  return msg.toLowerCase().includes('not_queued');
}

// ---------------------------------------------------------------------------
// Display helpers (shared by the widget, dispatch pop-up, tiles and display)
// ---------------------------------------------------------------------------

type NamedEntry = {
  customer_first_name?: string | null;
  customer_last_name?: string | null;
};

type PartyEntry = {
  person_index: number;
  party_size: number;
};

/** Public-safe name: first name + initial of the last name ("Dirk v."),
 *  mirrors booking.fn_public_display_name. Never a full surname. A last-name-
 *  only row (guest_name fallback) becomes "Jan J." when it has a space, else
 *  just the initial. */
export function displayName(e: NamedEntry): string {
  const first = (e.customer_first_name ?? '').trim();
  const last = (e.customer_last_name ?? '').trim();
  if (first) {
    return last ? `${first} ${last[0].toUpperCase()}.` : first;
  }
  if (last) {
    const sp = last.indexOf(' ');
    if (sp > 0) {
      const tail = last.slice(last.lastIndexOf(' ') + 1);
      return `${last.slice(0, sp)} ${tail[0].toUpperCase()}.`;
    }
    return `${last[0].toUpperCase()}.`;
  }
  return 'Gast';
}

/** Operator-facing full name ("Dirk van der Meulen"); "Gast" when unknown. */
export function fullName(e: NamedEntry): string {
  const s = `${(e.customer_first_name ?? '').trim()} ${(e.customer_last_name ?? '').trim()}`.trim();
  return s || 'Gast';
}

/** "2 van 3" for a member of a party, '' for a solo visitor. */
export function partyMarker(e: PartyEntry): string {
  return e.party_size > 1 ? `${e.person_index} van ${e.party_size}` : '';
}

/** "~15 min" / "nu" for the head; '' when unknown or parked (null). */
export function formatEta(etaMinutes: number | null | undefined): string {
  if (etaMinutes === null || etaMinutes === undefined) return '';
  if (etaMinutes <= 0) return 'nu';
  return `~${etaMinutes} min`;
}

/** The DB stores play languages lowercase ('nl'); the agent launch request and
 *  public.languages.short_name are UPPERCASE ('NL'). Every hand-off to the
 *  agent / release chips goes through here. */
export function agentLanguageCode(code: string | null | undefined): string | null {
  const c = (code ?? '').trim();
  return c ? c.toUpperCase() : null;
}

/** Case-insensitive equality of two language codes (null-safe). */
export function sameLanguage(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
}

/** Age helper for the undo affordances (RPC window = 5 minutes). */
export const UNDO_WINDOW_MS = 5 * 60_000;
export function withinUndoWindow(isoOrMs: string | number | null | undefined): boolean {
  if (isoOrMs === null || isoOrMs === undefined) return false;
  const t = typeof isoOrMs === 'number' ? isoOrMs : Date.parse(isoOrMs);
  return Number.isFinite(t) && Date.now() - t < UNDO_WINDOW_MS;
}

/** Operator prompt before undoing a dequeue that is not their own fresh
 *  dispatch (see QueueDispatchContext.undoLast needsConfirm). */
export function undoConfirmMessage(predicted: {
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  started_pc_name?: string | null;
} | null): string {
  if (!predicted) {
    return 'De laatste wachtrij-actie is niet van jou (collega of admin). Toch ongedaan maken?';
  }
  const pc = predicted.started_pc_name ? ` op ${predicted.started_pc_name}` : '';
  return `${fullName(predicted)} is gestart door een collega${pc}. Terug in de wachtrij zetten en de ervaring stoppen?`;
}
